import { TextOptions } from "@storebrand-digital/fms-translations/dist/helpers/helpers";

import {
  AlisContract,
  BankAccount,
  EkstrapensjonEmploymentContract,
  EpkContract,
  EpkEmploymentContract,
  EpkFleksibelContract,
  EpkFleksibelPkbContract,
  FmiContract,
  FripoliseContract,
  Holding,
  HybridMedGarantiContract,
  HybridMedInvesteringsvalgContract,
  HybridPensjonsbevisContract,
  IncomingContract as IncomingContractType,
  LinkContract,
  LinkOrganizationContract,
  Maybe,
  PensionFundItpContract,
  PensionFundUnfundedYtpUnderPayoutContract,
  PensionFundYtpContract,
  PkbContract,
  PrognosisRules,
  PublicPensionContract,
  UcitsAccount,
  UcitsAccountOnBehalfOfCustomer,
  UcitsOrganizationAccount,
  YtpContract
} from "@/@generated/graphql/types";

export enum Language {
  NO = "nb",
  EN = "en"
}

export type Nullable<T> = T | null | undefined;

export type PersonalFundContract = LinkContract | UcitsAccount | AlisContract;

export type ContractWithReturn = LinkContract | UcitsAccount;

export type OrganizationContract = LinkOrganizationContract | UcitsOrganizationAccount;

export type PensionContract =
  | EpkEmploymentContract
  | HybridMedGarantiContract
  | HybridPensjonsbevisContract
  | PensionContractWithContractNumber
  | PensionFundItpContract
  | PensionFundUnfundedYtpUnderPayoutContract
  | PublicPensionContract
  | YtpContract;

export type PensionContractWithContractNumber =
  | EpkContract
  | EpkFleksibelContract
  | EpkFleksibelPkbContract
  | FmiContract
  | HybridMedInvesteringsvalgContract
  | PkbContract;

export type FundContract = PersonalFundContract | OrganizationContract;

export type Contract =
  | FundContract
  | BankAccount
  | PensionContract
  | FmiContract
  | YtpContract
  | FripoliseContract
  | PensionFundYtpContract
  | EkstrapensjonEmploymentContract
  | UcitsAccount
  | UcitsOrganizationAccount
  | IncomingContractType
  | UcitsAccountOnBehalfOfCustomer;

export type LinkOrLinkOrganizationContract = LinkContract | LinkOrganizationContract;

export type ContractWithTransactions =
  | LinkContract
  | LinkOrganizationContract
  | EpkContract
  | EpkFleksibelContract
  | EpkFleksibelPkbContract
  | HybridMedInvesteringsvalgContract
  | PkbContract
  | FmiContract
  | EkstrapensjonEmploymentContract;

export type ContractWithContractNavigation =
  | AlisContract
  | BankAccount
  | EkstrapensjonEmploymentContract
  | EpkContract
  | EpkFleksibelContract
  | EpkFleksibelPkbContract
  | FmiContract
  | HybridMedInvesteringsvalgContract
  | LinkContract
  | LinkOrganizationContract
  | PensionContract
  | PkbContract
  | UcitsAccount
  | UcitsOrganizationAccount;

export type FMS = (key: string, textOptions?: TextOptions) => string;

export interface SavingsQueryResult {
  savingsEngagement?: {
    askAccounts?: UcitsAccount[];
    investorAccounts?: UcitsAccount[];
    fondskontoLinkContracts?: LinkContract[];
    garantiContracts?: LinkContract[];
    livrenteLinkContracts?: LinkContract[];
    bankAccounts?: BankAccount[];
    kapitalforsikringAlisContracts?: AlisContract[];
    livrenteAlisContracts?: AlisContract[];
    epkContracts?: EpkContract[];
    incomingContracts?: IncomingContractType[];
    epkFleksibelContracts?: EpkFleksibelContract[];
    epkEmploymentContracts?: EpkEmploymentContract[];
    epkFleksibelPkbContracts?: EpkFleksibelPkbContract[];
    hybridMedInvesteringsvalgContracts?: HybridMedInvesteringsvalgContract[];
    hybridMedGarantiContracts?: HybridMedGarantiContract[];
    hybridPensjonsbevisContracts?: HybridPensjonsbevisContract[];
    pkbContracts?: PkbContract[];
    fmiContracts?: FmiContract[];
    ytpContracts?: YtpContract[];
    ipsContracts?: LinkContract[];
    ipoContracts?: LinkContract[];
    ekstrapensjonContracts?: LinkContract[];
    ekstrapensjonEmploymentContracts?: EkstrapensjonEmploymentContract[];
    ipaLinkContracts?: LinkContract[];
    ipaAlisContracts?: AlisContract[];
    fripoliseContracts?: FripoliseContract[];
    pensionFundYtpContracts?: PensionFundYtpContract[];
    pensionFundUnfundedYtpContracts?: PensionFundYtpContract[];
    publicPensionContracts?: PublicPensionContract[];
    fmiMedGarantiContracts?: FripoliseContract[];
    pensionFundUnfundedItpContracts?: PensionFundItpContract[];
    pensionFundUnfundedYtpUnderPayoutContracts?: PensionFundUnfundedYtpUnderPayoutContract[];
    innskuddspensjonMedGarantiContracts?: FripoliseContract[];
    kollektivLivrenteContracts?: LinkContract[];
  };
  organizationEngagement?: {
    fondskontoLinkContracts?: LinkOrganizationContract[];
    investorAccounts?: UcitsOrganizationAccount[];
  };
  onBehalfOfEngagement?: {
    askAccounts?: UcitsAccountOnBehalfOfCustomer[];
    investorAccounts?: UcitsAccountOnBehalfOfCustomer[];
  };
}

/**
 * Naming convention:
 *
 * Use same name from savings-graph without "contract" or "account";
 * Use lowercase and add hyphen between words.
 */
export enum ContractType {
  FondskontoLink = "fondskonto-link",
  OrganizationFondskontoLink = "organization-fondskonto-link",
  Garanti = "garanti",
  LivrenteLink = "livrente-link",
  Bank = "sparekonto",
  KapitalforsikringAlis = "kapitalforsikring",
  LivrenteAlis = "livrente-alis",
  Epk = "epk",
  IncomingContract = "incoming-contract",
  EpkFleksibel = "epk-fleksibel",
  EpkFleksibelPkb = "epk-fleksibel-pkb",
  HybridMedInvesteringsvalg = "hybrid-med-investeringsvalg",
  HybridMedGaranti = "hybrid-med-garanti",
  HybridPensjonsbevis = "hybrid-pensjonsbevis",
  EpkEmployment = "epk-employment",
  Pkb = "pkb",
  Fmi = "fmi",
  Ytp = "ytp",
  Ekstrapensjon = "ekstrapensjon",
  EkstrapensjonEmployment = "ekstrapensjon-employment",
  Ipo = "ipo",
  IpaLink = "ipa-link",
  Ips = "ips",
  Fripolise = "fripolise",
  PensionFundYtp = "pension-fund-ytp",
  PensionFundUnfundedYtp = "pension-fund-unfunded-ytp",
  PublicPension = "public-pension",
  FmiMedGaranti = "fmi-med-garanti",
  PensionFundUnfundedItpContracts = "pension-fund-unfunded-itp",
  PensionFundUnfundedYtpUnderPayout = "pension-fund-unfunded-ytp-under-payout",
  InnskuddspensjonMedGaranti = "innskudd-pensjon-med-garanti",
  IpaAlis = "ipa-alis",
  KollektivLivRente = "kollektiv-livrente"
}

export type LinkContractType =
  | ContractType.FondskontoLink
  | ContractType.Garanti
  | ContractType.LivrenteLink
  | ContractType.Ekstrapensjon
  | ContractType.Ipo
  | ContractType.IpaLink
  | ContractType.Ips
  | ContractType.KollektivLivRente;

export type AlisContractType = ContractType.KapitalforsikringAlis | ContractType.LivrenteAlis | ContractType.IpaAlis;

export type PensionFundYtpContractType = ContractType.PensionFundYtp | ContractType.PensionFundUnfundedYtp;

export enum SupportedSearchParam {
  cmid = "cmid",
  displayMode = "displaymode",
  exitUrl = "exiturl",
  partnerId = "partnerid",
  logoutUrl = "logouturl"
}

/* This is used in containers for contract detail pages to avoid ts-errors when getting contractNumber with useParams-hook.
 * The index signature keeps it assignable to react-router's `useParams<T extends Record<string, string | undefined>>`
 * constraint — an interface (unlike a type alias) has no implicit index signature, so it must be declared explicitly.
 */
export interface ContractDetailParams {
  contractNumber: string;
  [key: string]: string | undefined;
}

export interface ContractWithHoldings {
  holdings?: Holding;
  prognosisRules: PrognosisRules;
}

export interface ContractWithProductNameFmsKeyAndProductCode {
  productNameFms?: Maybe<{ fmsKey?: Maybe<string> }>;
  productCode?: Maybe<string>;
}

export interface ContractWithProductNameFmsKeyAndProductCodeAndFees {
  productNameFms?: Maybe<{ fmsKey?: Maybe<string> }>;
  productCode?: Maybe<string>;
  contractManagementFeePercentage?: Maybe<number>;
  administrationFee?: Maybe<{
    label?: Maybe<string>;
    code?: Maybe<string>;
    fmsKey?: Maybe<string>;
  }>;
}

/**
 * allocationCode values
 * AS1 - Anbefalt sparing,
 * LN0 - Freeze and life long reduction/"anbefealt pensjon",
 * LN3 - life long reduction/"anbefalt pensjon",
 * REBA1 - Rebalancing, STDNED1 - 10 year long step down,
 * STDNED2 - 10 year long step down,
 * STDNED3 - 10 year long step down,
 * TOMAL - No life long reduction and no step down. Free choosing of funds
 * */
export interface IdentifiableContract {
  id: string;
  allocationCode?: string;
}

export interface AccountIdContract {
  accountId?: Maybe<string>;
}

export type UcitsContract = UcitsAccount | UcitsOrganizationAccount | UcitsAccountOnBehalfOfCustomer;

export enum PartnerId {
  Eika = "eika",
  None = "none"
}
